import type { MutableRefObject } from "react";
import type { MfgMaterial, MfgPlant } from "@/lib/manufacturingClient";
import type { MfgPurchaseOrder, MfgVendor } from "@/lib/manufacturing/purchaseOrderClient";
import type { MfgVendorExtraContact } from "@/lib/manufacturing/vendorExtraContacts";
import type { PoDraftLine } from "@/components/manufacturing/purchaseOrderDraftState";
import { GeneratePurchaseOrderLinesEditor } from "@/components/manufacturing/GeneratePurchaseOrderLinesEditor";
import { GeneratePurchaseOrderQueue } from "@/components/manufacturing/GeneratePurchaseOrderQueue";
import {
  GeneratePurchaseOrderHeader,
  type SavePlantDeliveryAddress,
} from "@/components/manufacturing/GeneratePurchaseOrderHeader";
import { GeneratePurchaseOrderDictate } from "@/components/manufacturing/GeneratePurchaseOrderDictate";
import { GeneratePurchaseOrderFooter } from "@/components/manufacturing/GeneratePurchaseOrderFooter";
import { countUnacknowledgedPlantRequests } from "@/lib/manufacturing/plantMaterialRequest";

export function GeneratePurchaseOrderComposer(props: {
  orders: MfgPurchaseOrder[];
  active: MfgPurchaseOrder | null;
  busy: boolean;
  reviewRequests?: boolean;
  vendors: MfgVendor[];
  plants: MfgPlant[];
  vendorId: string;
  setVendorId: (id: string) => void;
  mergeVendorCc: (contacts: MfgVendorExtraContact[] | undefined) => void;
  plantId: string;
  setPlantId: (id: string) => void;
  shipTo: string;
  setShipTo: (v: string) => void;
  shipToOpen: boolean;
  setShipToOpen: (v: boolean) => void;
  poDate: string;
  setPoDate: (v: string) => void;
  enabled: boolean;
  readOnlyHeader: boolean;
  materials: MfgMaterial[];
  lines: PoDraftLine[];
  setLines: (lines: PoDraftLine[]) => void;
  confirmation: string;
  setConfirmation: (v: string) => void;
  ccEmails: string[];
  setCcEmails: (v: string[]) => void;
  msg: { text: string; ok: boolean } | null;
  setMsg: (msg: { text: string; ok: boolean } | null) => void;
  canIssue: boolean;
  expectedDeliveryOn: string;
  setExpectedDeliveryOn: (v: string) => void;
  defaultInvoiceCcEmails: string[];
  onNew: () => void;
  onOpen: (id: string) => void;
  onArchived: (order: MfgPurchaseOrder) => void;
  onDeleted: (poId: string) => void;
  onVendorCreated: (vendor: MfgVendor) => void;
  onPlantUpdated: (plant: MfgPlant) => void;
  savePlantDeliveryAddressRef: MutableRefObject<SavePlantDeliveryAddress>;
  onVendorsChanged: (vendors: MfgVendor[]) => void;
  onDictateApply: (next: {
    vendorId?: string;
    unmatchedVendorName?: string;
    lines?: PoDraftLine[];
  }) => void;
  onSaveDraft: () => void;
  onIssueAndPdf: () => void;
  onFinalize: () => void;
  canFinalize: boolean;
  finalized: boolean;
  onAcknowledge: () => void;
  onReload: () => void;
  onUpdated: (order: MfgPurchaseOrder) => void;
}) {
  return (
    <div className="mt-3 space-y-3">
      <GeneratePurchaseOrderQueue
        orders={props.orders}
        activeId={props.active?.id ?? ""}
        busy={props.busy}
        onNew={props.onNew}
        onOpen={props.onOpen}
        onArchived={props.onArchived}
        onDeleted={props.onDeleted}
        onError={(text) => props.setMsg({ text, ok: false })}
        initialFilter={
          props.reviewRequests || countUnacknowledgedPlantRequests(props.orders) > 0
            ? "requests"
            : "open"
        }
      />
      <div className="grid items-start gap-3 md:grid-cols-2">
        <GeneratePurchaseOrderHeader
          vendors={props.vendors}
          plants={props.plants}
          vendorId={props.vendorId}
          setVendorId={props.setVendorId}
          mergeVendorCc={props.mergeVendorCc}
          plantId={props.plantId}
          setPlantId={props.setPlantId}
          shipTo={props.shipTo}
          setShipTo={props.setShipTo}
          shipToOpen={props.shipToOpen}
          setShipToOpen={props.setShipToOpen}
          poDate={props.poDate}
          setPoDate={props.setPoDate}
          enabled={props.enabled}
          busy={props.busy}
          readOnlyHeader={props.readOnlyHeader}
          onVendorCreated={props.onVendorCreated}
          onPlantUpdated={props.onPlantUpdated}
          savePlantDeliveryAddressRef={props.savePlantDeliveryAddressRef}
        />
        {!props.readOnlyHeader ? (
          <GeneratePurchaseOrderDictate
            enabled={props.enabled}
            busy={props.busy}
            materials={props.materials}
            vendors={props.vendors}
            plantId={props.plantId}
            onVendorsChanged={props.onVendorsChanged}
            onApply={props.onDictateApply}
          />
        ) : null}
      </div>

      <GeneratePurchaseOrderLinesEditor
        lines={props.lines}
        onChange={props.setLines}
        materials={props.materials}
        enabled={props.enabled}
        busy={props.busy}
        readOnlyStructure={props.readOnlyHeader}
      />
      <GeneratePurchaseOrderFooter
        materials={props.materials}
        plantId={props.plantId}
        lines={props.lines}
        onLines={props.setLines}
        confirmation={props.confirmation}
        setConfirmation={props.setConfirmation}
        ccEmails={props.ccEmails}
        setCcEmails={props.setCcEmails}
        enabled={props.enabled}
        busy={props.busy}
        readOnlyHeader={props.readOnlyHeader}
        hasIssuedActive={Boolean(props.active && props.active.status !== "draft")}
        msg={props.msg}
        onMsg={(m) => props.setMsg(m)}
        onSaveDraft={props.onSaveDraft}
        onIssueAndPdf={props.onIssueAndPdf}
        onFinalize={props.onFinalize}
        canFinalize={props.canFinalize}
        finalized={props.finalized}
        canIssue={props.canIssue}
        active={props.active}
        expectedDeliveryOn={props.expectedDeliveryOn}
        onExpectedDeliveryOn={props.setExpectedDeliveryOn}
        onAcknowledge={props.onAcknowledge}
        onReload={props.onReload}
        onUpdated={props.onUpdated}
        defaultInvoiceCcEmails={props.defaultInvoiceCcEmails}
      />
    </div>
  );
}
